import React, { useCallback, useEffect, useMemo, useState } from "react";
import API from "../api/api";

const severityClass = (severity) => `severity-${String(severity || "info").toLowerCase()}`;

const apiErrorMessage = async (error, fallback) => {
  const payload = error.response?.data;

  // PDF downloads request a Blob, including when the server returns JSON for
  // an error. Decode that JSON so a 503 explains the missing server package.
  if (typeof Blob !== "undefined" && payload instanceof Blob) {
    try {
      const data = JSON.parse(await payload.text());
      if (data?.error) {
        return data.error;
      }
    } catch {
      // Use the normal fallback when the response is not JSON.
    }
  }

  return payload?.error || fallback;
};

function AdminPanel({ role }) {
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem("cybersec_active_tab");
    return ["console", "reports", "users"].includes(savedTab) ? savedTab : "console";
  });
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [summary, setSummary] = useState(null);
  const [message, setMessage] = useState("");
  const [resumingJobId, setResumingJobId] = useState(null);
  const [scanJobs, setScanJobs] = useState([]);
  const [selectedConsoleJobId, setSelectedConsoleJobId] = useState(() => {
    const storedValue = localStorage.getItem("cybersec_selected_console_job_id");
    return storedValue ? Number(storedValue) : null;
  });
  const [newUser, setNewUser] = useState({
    username: "",
    password: "",
    role: "user"
  });

  const fetchUsers = useCallback(() => {
    API.get("/api/admin/users")
      .then(res => setUsers((res.data || []).map(user => ({ ...user, password: "" }))))
      .catch(err => setMessage(err.response?.data?.error || "Unable to load users"));
  }, []);

  const fetchLegacyReports = useCallback(() => {
    API.get("/api/admin/scans")
      .then(res => {
        const rows = res.data || [];
        const mostRecentByTarget = rows.reduce((uniqueRows, row) => {
          if (!uniqueRows.has(row.target)) {
            uniqueRows.set(row.target, row);
          }
          return uniqueRows;
        }, new Map());
        setReports([...mostRecentByTarget.values()].map(row => ({
          id: `legacy-${row.id}`,
          target: row.target,
          risk_score: row.state === "open" ? 25 : 5,
          severity: row.state === "open" ? "Medium" : "Low",
          created_at: "",
          report: {
            target: row.target,
            risk_score: row.state === "open" ? 25 : 5,
            severity: row.state === "open" ? "Medium" : "Low",
            findings: [{
              title: `${row.host}:${row.port}/${row.protocol}`,
              severity: row.state === "open" ? "Medium" : "Info",
              description: row.state,
              evidence: row.service || "unknown",
              fix: "Review the service and restrict access where possible."
            }],
            tools: []
          }
        })));
      })
      .catch(err => setMessage(err.response?.data?.error || "Unable to load reports"));
  }, []);

  const fetchReports = useCallback(() => {
    API.get("/api/admin/reports")
      .then(res => setReports(res.data || []))
      .catch(() => fetchLegacyReports());

    API.get("/api/admin/reports/summary")
      .then(res => setSummary(res.data))
      .catch(() => setSummary(null));
  }, [fetchLegacyReports]);

  const fetchScanJobs = useCallback(() => {
    API.get("/api/admin/scan-jobs")
      .then(res => {
        const rows = res.data || [];
        const nextJobs = rows.filter(job => ["queued", "running", "completed", "failed"].includes(job.status)).slice(0, 10);
        setScanJobs(nextJobs);
        setSelectedConsoleJobId(currentId => {
          const existingJob = nextJobs.find(job => job.id === currentId);
          if (existingJob) {
            return currentId;
          }
          return nextJobs[0]?.id || null;
        });
        if (nextJobs.length > 0) {
          setActiveTab("console");
        }
      })
      .catch(err => setMessage(err.response?.data?.error || "Unable to restore scan status"));
  }, []);

  useEffect(() => {
    fetchReports();
    fetchScanJobs();
    if (role === "admin") {
      fetchUsers();
    }
  }, [fetchReports, fetchScanJobs, fetchUsers, role]);

  useEffect(() => {
    localStorage.setItem("cybersec_active_tab", activeTab);
  }, [activeTab]);

  useEffect(() => {
    if (selectedConsoleJobId) {
      localStorage.setItem("cybersec_selected_console_job_id", String(selectedConsoleJobId));
      return;
    }

    localStorage.removeItem("cybersec_selected_console_job_id");
  }, [selectedConsoleJobId]);

  useEffect(() => {
    const activeJobs = scanJobs.filter(job => ["queued", "running"].includes(job.status));
    if (activeJobs.length === 0) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      Promise.all(activeJobs.map(job => API.get(`/api/admin/scan-jobs/${job.id}`)))
        .then(responses => {
          const updatedJobs = responses.map(res => res.data);
          setScanJobs(currentJobs =>
            currentJobs.map(job => updatedJobs.find(updatedJob => updatedJob.id === job.id) || job)
          );
          if (updatedJobs.some(job => job.status === "completed")) {
            fetchReports();
          }
        })
        .catch(() => {
          setMessage("Unable to refresh scan status");
          window.clearInterval(timer);
        });
    }, 1500);

    return () => window.clearInterval(timer);
  }, [fetchReports, scanJobs]);

  const sortedReports = useMemo(() => [...reports].sort((left, right) => {
    const leftTime = left.created_at ? new Date(left.created_at).getTime() : 0;
    const rightTime = right.created_at ? new Date(right.created_at).getTime() : 0;
    return rightTime - leftTime;
  }), [reports]);

  const consoleJobs = useMemo(() => {
    return [...scanJobs].sort((left, right) => {
      const leftTime = left.created_at ? new Date(left.created_at).getTime() : 0;
      const rightTime = right.created_at ? new Date(right.created_at).getTime() : 0;
      return rightTime - leftTime;
    });
  }, [scanJobs]);

  const selectedConsoleJob = useMemo(() => {
    if (consoleJobs.length === 0) {
      return null;
    }

    return consoleJobs.find(job => job.id === selectedConsoleJobId) || consoleJobs[0];
  }, [consoleJobs, selectedConsoleJobId]);

  const canResumeJob = (job) => ["failed", "paused"].includes(job?.status);

  const resumeScan = (jobId) => {
    if (!jobId) {
      return;
    }

    setMessage("");
    setResumingJobId(jobId);
    API.post(`/api/admin/scan-jobs/${jobId}/resume`)
      .then(res => {
        const resumedJob = res.data;
        setScanJobs(currentJobs =>
          currentJobs.map(job => job.id === resumedJob.id ? resumedJob : job)
        );
        setSelectedConsoleJobId(resumedJob.id);
        setActiveTab("console");
        setMessage("Scan resumed");
      })
      .catch(err => setMessage(err.response?.data?.error || "Unable to resume scan"))
      .finally(() => setResumingJobId(null));
  };

  const createUser = () => {
    setMessage("");
    API.post("/api/admin/users", newUser)
      .then(() => {
        fetchUsers();
        setNewUser({ username: "", password: "", role: "user" });
        setMessage("User created");
      })
      .catch(err => setMessage(err.response?.data?.error || "Unable to create user"));
  };

  const updateUserField = (id, key, value) => {
    setUsers(currentUsers =>
      currentUsers.map(user => user.id === id ? { ...user, [key]: value } : user)
    );
  };

  const saveUser = (user) => {
    setMessage("");
    API.put(`/api/admin/users/${user.id}`, {
      username: user.username,
      password: user.password,
      role: user.role
    })
      .then(() => {
        fetchUsers();
        setMessage("User updated");
      })
      .catch(err => setMessage(err.response?.data?.error || "Unable to update user"));
  };

  const deleteUser = (id) => {
    setMessage("");
    API.delete(`/api/admin/users/${id}`)
      .then(() => {
        fetchUsers();
        setMessage("User deleted");
      })
      .catch(err => setMessage(err.response?.data?.error || "Unable to delete user"));
  };


  const downloadReportsPdf = async ({ date, target } = {}) => {
    try {
      const response = await API.get("/api/admin/reports/export", {
        params: {
          ...(date ? { date } : {}),
          ...(target ? { target } : {}),
        },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `CyberSec_Report_${target || date || "latest"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setMessage(await apiErrorMessage(err, "Unable to generate the PDF report"));
    }
  };

  const exportPdf = (reportItem) => {
    downloadReportsPdf({ target: reportItem?.target });
  };

  return (
    <section className="card admin-dashboard">
      <div className="section-heading">
        <div>
          <h2>{role === "admin" ? "Admin Dashboard" : "Security Console"}</h2>
          <p>Monitor live scan output and export saved reports.</p>
        </div>
        <div className="tabs">
          <button type="button" className={activeTab === "console" ? "active" : ""} onClick={() => setActiveTab("console")}>Console</button>
          <button type="button" className={activeTab === "reports" ? "active" : ""} onClick={() => { setActiveTab("reports"); fetchReports(); }}>Reports</button>
          {role === "admin" && (
            <button type="button" className={activeTab === "users" ? "active" : ""} onClick={() => setActiveTab("users")}>Users</button>
          )}
        </div>
      </div>

      {message && <p className="status-message">{message}</p>}

      {activeTab === "console" && (
        <div className="console-layout">
          <aside className="console-sidebar">
            <div className="section-heading compact-heading">
              <div>
                <h3>Scan Sessions</h3>
                <p>Pick a target to watch its live output.</p>
              </div>
            </div>
            <div className="console-job-list">
              {consoleJobs.map(job => (
                <button
                  type="button"
                  key={job.id}
                  className={selectedConsoleJob?.id === job.id ? "console-job-card active" : "console-job-card"}
                  onClick={() => setSelectedConsoleJobId(job.id)}
                >
                  <strong>{job.target}</strong>
                  <span>{job.status} • {job.progress || 0}%</span>
                  <small>{job.current_tool || "Waiting for scan runtime"}</small>
                </button>
              ))}
              {consoleJobs.length === 0 && (
                <div className="empty-console-state">
                  <p className="muted-text">No scan jobs yet</p>
                  <p className="muted-text">Start a scan from New Scan in the top navigation.</p>
                </div>
              )}
            </div>
          </aside>

          <section className="console-main">
            {selectedConsoleJob ? (
              <>
                <div className="scan-status-panel console-status-panel">
                  <div className="scan-status-header">
                    <div>
                      <strong>{selectedConsoleJob.target}</strong>
                      <span>{selectedConsoleJob.status} - {selectedConsoleJob.current_tool || "Preparing scan"}</span>
                    </div>
                    <div className="scan-status-actions">
                      {canResumeJob(selectedConsoleJob) && (
                        <button
                          type="button"
                          className="resume-scan-button"
                          disabled={resumingJobId === selectedConsoleJob.id}
                          onClick={() => resumeScan(selectedConsoleJob.id)}
                        >
                          {resumingJobId === selectedConsoleJob.id ? "Resuming..." : "Resume"}
                        </button>
                      )}
                      <b>{selectedConsoleJob.progress || 0}%</b>
                    </div>
                  </div>
                  <div className="progress-track">
                    <div style={{ width: `${selectedConsoleJob.progress || 0}%` }} />
                  </div>

                  <div className="tool-progress-list">
                    {(selectedConsoleJob.tool_progress || selectedConsoleJob.report?.tool_progress || []).map(tool => (
                      <div className="tool-progress-row" key={`${selectedConsoleJob.id}-${tool.id}`}>
                        <div>
                          <strong>{tool.name}</strong>
                          <span>{tool.status}</span>
                        </div>
                        <div className="tool-progress-meter">
                          <div className="progress-track small-track">
                            <div style={{ width: `${tool.progress || 0}%` }} />
                          </div>
                          <b>{tool.progress || 0}%</b>
                        </div>
                      </div>
                    ))}
                  </div>
                  {selectedConsoleJob.error && <p className="form-error">{selectedConsoleJob.error}</p>}
                </div>

                <div className="console-screen">
                  <div className="console-screen-header">
                    <strong>Runtime Console</strong>
                    <span>{selectedConsoleJob.console_lines?.length || 0} line(s)</span>
                  </div>
                  <div className="terminal console-terminal">
                    {(selectedConsoleJob.console_lines || []).length > 0 ? (
                      (selectedConsoleJob.console_lines || []).map((line, index) => (
                        <div key={`${selectedConsoleJob.id}-line-${index}`}>{line}</div>
                      ))
                    ) : (
                      <div>Waiting for console output...</div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="console-screen">
                <div className="console-screen-header">
                  <strong>Runtime Console</strong>
                  <span>Ready</span>
                </div>
                <div className="terminal console-terminal">
                  <div>Start a scan from New Scan in the top navigation. This console will reopen after refresh or login.</div>
                </div>
              </div>
            )}
          </section>
        </div>
      )}

      {activeTab === "reports" && (
        <>
          <div className="report-actions">
            <div className="metric-row">
              <div><span>{summary?.total_results || reports.length}</span><small>Reports</small></div>
              <div><span>{summary?.high_reports || 0}</span><small>High risk</small></div>
              <div><span>{summary?.targets?.length || 0}</span><small>Targets</small></div>
            </div>
          </div>

          <div className="admin-reports-list">
            {sortedReports.map(reportItem => (
              <article className="admin-reports-row" key={reportItem.id}>
                <div>
                  <h3>{reportItem.target}</h3>
                  <p>{reportItem.created_at ? new Date(reportItem.created_at).toLocaleString() : "Legacy scan result"}</p>
                </div>
                <span className={severityClass(reportItem.severity)}>{reportItem.severity}</span>
                <div className="admin-reports-risk">
                  <strong>{reportItem.risk_score}</strong>
                  <span>Risk score</span>
                </div>
                <button type="button" onClick={() => exportPdf(reportItem)}>Export PDF</button>
              </article>
            ))}
            {sortedReports.length === 0 && <p className="muted-text">No scan reports yet</p>}
          </div>
        </>
      )}

      {role === "admin" && activeTab === "users" && (
        <>
          <div className="admin-form">
            <input placeholder="Username" value={newUser.username} onChange={e => setNewUser({ ...newUser, username: e.target.value })} />
            <input placeholder="Password" type="password" value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} />
            <select value={newUser.role} onChange={e => setNewUser({ ...newUser, role: e.target.value })}>
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
            <button type="button" onClick={createUser} disabled={!newUser.username || !newUser.password}>Create User</button>
          </div>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Username</th>
                  <th>New Password</th>
                  <th>Access Type</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id}>
                    <td>{user.id}</td>
                    <td><input value={user.username} onChange={e => updateUserField(user.id, "username", e.target.value)} /></td>
                    <td><input placeholder="Leave blank to keep" type="password" value={user.password} onChange={e => updateUserField(user.id, "password", e.target.value)} /></td>
                    <td>
                      <select value={user.role} onChange={e => updateUserField(user.id, "role", e.target.value)}>
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                    <td className="action-cell">
                      <button type="button" onClick={() => saveUser(user)}>Save</button>
                      <button type="button" className="danger-button" onClick={() => deleteUser(user.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

export default AdminPanel;
